import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, PhoneOff, Loader2, Radio } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

const OPENAI_CALLS = "https://api.openai.com/v1/realtime/calls";

const mainRoot = () => document.querySelector("[data-octon-main]") || document.querySelector("main") || document.body;
const clean = (t) => (t || "").replace(/\s+/g, " ").trim();
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const waitFor = async (pred, timeout, every = 250) => {
  const end = Date.now() + timeout;
  let v = pred();
  while (!v && Date.now() < end) { await sleep(every); v = pred(); }
  return v;
};

// Capture the exact visible text of the current screen so OCTON is "aware of every word".
const captureScreen = () => {
  let text = (mainRoot()?.innerText || "").replace(/\n{3,}/g, "\n\n").trim();
  if (text.length > 9000) text = text.slice(0, 9000) + "…";
  return { path: window.location.pathname, title: document.title, text };
};

// Screen inventory — every anchored section (data-octon-section) and whether its data is actually rendered.
const sectionLabel = (el) => el.getAttribute("data-octon-label") || clean(el.textContent).slice(0, 80);
const inventory = () => Array.from(mainRoot().querySelectorAll("[data-octon-section]")).map((el) => ({
  el, label: sectionLabel(el),
  keys: (el.getAttribute("data-octon-section") || "").toLowerCase(),
  state: el.getAttribute("data-octon-state") || "ready",
}));
const fixtureOnScreen = () => {
  const lbl = document.querySelector("[data-octon-fixture-label]")?.getAttribute("data-octon-fixture-label");
  if (lbl && lbl.trim() && !/^\s*vs\s*$/.test(lbl) && !lbl.startsWith(" vs")) return clean(lbl);
  const h = document.querySelector("[data-testid='lineup-club-home']");
  const a = document.querySelector("[data-testid='lineup-club-away']");
  return h && a ? `${clean(h.textContent)} vs ${clean(a.textContent)}` : null;
};
// Overlays (player card / dossier) sit ABOVE the page — she must know what's open on top and be able to close it.
const openOverlays = () => Array.from(document.querySelectorAll("[data-octon-overlay]")).map((el) => ({
  el, label: el.getAttribute("data-octon-label") || "card", state: el.getAttribute("data-octon-state") || "ready",
}));
// The TRUTH about the screen: what the coach can see right now vs what is still building.
const screenReport = () => {
  const inv = inventory();
  const on_screen = inv.filter((s) => s.state === "ready").map((s) => s.label);
  const still_loading = inv.filter((s) => s.state === "loading").map((s) => s.label);
  const failed = inv.filter((s) => s.state === "error").map((s) => s.label);
  if (!inv.length) {
    Array.from(mainRoot().querySelectorAll("h1,h2,h3")).slice(0, 8).forEach((h) => { const t = clean(h.textContent); if (t) on_screen.push(t); });
  }
  const busy = !!mainRoot().querySelector("[data-octon-state='loading'], [data-testid='fixture-loading']");
  const fx = fixtureOnScreen();
  const ov = openOverlays();
  const open_card = ov.length ? `${ov[ov.length - 1].label}${ov[ov.length - 1].state === "loading" ? " (loading)" : ""}` : null;
  // Where each section sits relative to the viewport — so "scroll further" / "the one below" are facts, not guesses.
  const vh = window.innerHeight;
  const where = (el) => { const r = el.getBoundingClientRect(); if (r.bottom < 80) return "above"; if (r.top > vh - 40) return "below"; return "in_view"; };
  const sections = inv.map((s) => ({ label: s.label, state: s.state, where: where(s.el) }));
  const glowEl = document.querySelector(".octon-glacier");
  const glowing = glowEl ? (glowEl.getAttribute("data-octon-label") || clean(glowEl.textContent).slice(0, 60)) : null;
  const se = document.scrollingElement || document.documentElement;
  const maxScroll = Math.max(1, se.scrollHeight - vh);
  const scroll = { pct: Math.round((se.scrollTop / maxScroll) * 100), at_top: se.scrollTop < 40, at_bottom: se.scrollTop >= maxScroll - 40 };
  return { page: window.location.pathname, ...(fx ? { fixture_on_screen: fx } : {}), open_card, on_screen, still_loading, failed, busy, sections, glowing, scroll };
};
const findAnchor = (rawLabel) => {
  const label = (rawLabel || "").toLowerCase().trim();
  if (!label) return null;
  if (["highlighted", "glowing", "spotlight", "this", "that", "selected", "current"].includes(label)) {
    const g = document.querySelector(".octon-glacier");
    return g ? { el: g, label: g.getAttribute("data-octon-label") || clean(g.textContent).slice(0, 80), keys: label, state: g.getAttribute("data-octon-state") || "ready" } : null;
  }
  const inv = inventory();
  const byLabel = inv.find((s) => (s.label || "").toLowerCase() === label);
  if (byLabel) return byLabel;
  const contains = inv.filter((s) => s.keys.includes(label) || (s.label || "").toLowerCase().includes(label));
  if (contains.length) {
    // prefer a LABEL hit, then the MOST SPECIFIC card (a sub-card inside a bigger card wins over its parent)
    const labelHits = contains.filter((s) => (s.label || "").toLowerCase().includes(label));
    const pool = labelHits.length ? labelHits : contains;
    return pool.find((s) => !pool.some((o) => o !== s && s.el.contains(o.el))) || pool[0];
  }
  // Score by how many query words each section matches (label words count double); ties → DOM order.
  const words = label.split(/[^a-z0-9]+/).filter((w) => w.length > 2);
  if (!words.length) return null;
  let best = null, bestScore = 0;
  for (const s of inv) {
    const lbl = (s.label || "").toLowerCase();
    let score = words.reduce((acc, w) => acc + (lbl.includes(w) ? 2 : 0) + (s.keys.includes(w) ? 1 : 0), 0);
    if (score && inv.some((o) => o !== s && s.el.contains(o.el) && words.some((w) => (o.label || "").toLowerCase().includes(w)))) score -= 0.5; // parent of a more specific hit
    if (score > bestScore) { best = s; bestScore = score; }
  }
  // demand that the best match covers (nearly) all the query words, else refuse rather than glow the wrong card
  const covered = best ? words.filter((w) => (best.label || "").toLowerCase().includes(w) || best.keys.includes(w)).length : 0;
  const need = words.length <= 2 ? words.length : Math.ceil(words.length * 2 / 3);
  return best && covered >= need ? best : null;
};

// GENERIC HANDS — every button / dropdown / slider / field on the current page, so the coach never needs a
// dedicated tool per control. listControls() describes them; useControl() clicks / sets them like a finger would.
const isVisible = (el) => !!(el.getClientRects().length && getComputedStyle(el).visibility !== "hidden");
const controlLabel = (el) => {
  const tag = el.tagName;
  const isField = tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA";
  if (tag === "BUTTON" && el.getAttribute("aria-haspopup") === "listbox") {
    const capEl = el.parentElement?.parentElement?.querySelector("label, .font-tac");
    const cap = capEl ? clean(capEl.textContent) : "";
    return `${cap ? cap + ": " : ""}${clean(el.innerText)}`.slice(0, 60);
  }
  const own = clean(el.getAttribute("aria-label") || el.getAttribute("title") || (isField ? "" : el.innerText));
  if (own) return own.slice(0, 60);
  // Fields: the caption sitting above/beside them (label / .font-tac span in the same wrapper).
  const wrap = el.closest("label") || el.parentElement;
  const cap = wrap ? Array.from(wrap.querySelectorAll("label, .font-tac, span, p")).find((n) => n !== el && !n.contains(el) && clean(n.textContent)) : null;
  const near = cap ? clean(cap.textContent) : "";
  return (near || clean(el.getAttribute("placeholder") || "") || clean((el.getAttribute("data-testid") || "").replace(/[-_]/g, " "))).slice(0, 60);
};
const isPickerEl = (el) => el.tagName === "BUTTON" && el.getAttribute("aria-haspopup") === "listbox";
const listControls = () => {
  const root = mainRoot();
  const els = Array.from(root.querySelectorAll("button, select, input, textarea, [role='button'], [role='switch'], a[href]"))
    .filter((el) => isVisible(el) && !el.closest("[data-testid='octon-voice-orb']") && !/^(team|opponent|league)-option-/.test(el.getAttribute("data-testid") || ""));
  const out = [];
  for (const el of els.slice(0, 80)) {
    const tid = el.getAttribute("data-testid") || "";
    const tag = el.tagName;
    const c = { id: tid || controlLabel(el).toLowerCase().replace(/[^a-z0-9]+/g, "-"), label: controlLabel(el) };
    if (tag === "SELECT") {
      c.type = "select";
      c.value = el.selectedOptions[0] ? clean(el.selectedOptions[0].textContent) : el.value;
      c.options = Array.from(el.options).slice(0, 25).map((o) => clean(o.textContent));
    } else if (tag === "INPUT") {
      const t = (el.getAttribute("type") || "text").toLowerCase();
      c.type = t === "range" ? "slider" : t === "checkbox" ? "checkbox" : t;
      if (t === "range") Object.assign(c, { value: el.value, min: el.min, max: el.max });
      else if (t === "checkbox") c.checked = el.checked;
      else c.value = el.value;
    } else if (tag === "TEXTAREA") { c.type = "text"; c.value = el.value.slice(0, 80); }
    else if (tag === "A") { c.type = "link"; }
    else {
      c.type = isPickerEl(el) ? "picker" : "button";
      if (c.type === "picker") { c.value = clean(el.innerText); c.hint = "set with a club/league name"; }
      if (el.getAttribute("aria-pressed")) c.pressed = el.getAttribute("aria-pressed") === "true";
    }
    if (el.disabled) c.disabled = true;
    const sec = el.closest("[data-octon-section]");
    if (sec) c.section = sectionLabel(sec);
    if (c.label) out.push(c);
  }
  return out;
};
const findControl = (query) => {
  const q = (query || "").toLowerCase().trim();
  if (!q) return null;
  const qw = q.split(/[^a-z0-9]+/).filter(Boolean);
  const esc = (w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const hasWord = (txt, w) => new RegExp("(^|[^a-z0-9])" + esc(w) + "([^a-z0-9]|$)").test(txt);
  const root = mainRoot();
  const els = Array.from(root.querySelectorAll("button, select, input, textarea, [role='button'], [role='switch'], a[href]"))
    .filter((el) => isVisible(el) && !el.closest("[data-testid='octon-voice-orb']"));
  let best = null, bestScore = 0;
  for (const el of els) {
    const tid = (el.getAttribute("data-testid") || "").toLowerCase();
    const tidWords = tid.replace(/[-_]/g, " ");
    const label = controlLabel(el).toLowerCase();
    let score = 0;
    if (tid && tid === q) score = 100;
    else if (label === q) score = 95;
    else {
      const hitT = qw.filter((w) => hasWord(tidWords, w)).length / qw.length;
      const hitL = qw.filter((w) => hasWord(label, w)).length / qw.length;
      score = Math.max(hitT * 80, hitL * 75);
      if (label.includes(q) || tidWords.includes(q)) score = Math.max(score, 70);
    }
    if (score > bestScore) { bestScore = score; best = el; }
  }
  return bestScore >= 50 ? best : null;
};
const setNative = (el, value, evt) => {
  const proto = el.tagName === "SELECT" ? HTMLSelectElement.prototype : el.tagName === "TEXTAREA" ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, "value").set.call(el, String(value));
  el.dispatchEvent(new Event(evt, { bubbles: true }));
  if (evt === "input") el.dispatchEvent(new Event("change", { bubbles: true }));
};
const operateControl = async (query, action, value) => {
  const el = findControl(query);
  if (!el) {
    const avail = listControls().map((c) => c.label).filter(Boolean).slice(0, 40);
    return { ok: false, error: `No control matching "${query}" on this page.`, available_controls: avail };
  }
  const tid = el.getAttribute("data-testid") || "";
  const label = controlLabel(el);
  if (el.disabled) return { ok: false, error: `"${label}" is disabled right now (not usable yet).` };
  const act = (action || (value !== undefined && value !== null && value !== "" ? "set" : "click")).toLowerCase();
  const tag = el.tagName;
  const isPicker = isPickerEl(el);
  if (isPicker && value !== undefined && value !== null && value !== "") {
    // Custom searchable picker (club / league): open → search → pick the matching option.
    el.click();
    const search = await waitFor(() => document.querySelector(`[data-testid='${tid}-search']`), 1500, 100);
    if (search) setNative(search, value, "input");
    const opt = await waitFor(() => {
      const opts = Array.from(document.querySelectorAll(`[data-testid='${tid}-panel'] button`));
      return opts.find((o) => clean(o.textContent).toLowerCase().includes(String(value).toLowerCase())) || null;
    }, 2500, 150);
    if (!opt) {
      if (search) setNative(search, "", "input");
      document.body.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
      return { ok: false, error: `"${value}" is not an option in ${label}.` };
    }
    opt.click();
    await sleep(500);
    return { ok: true, control: label, set_to: clean(opt.textContent), ...viewState(null) };
  }
  if (act === "click" || tag === "BUTTON" || el.getAttribute("role") === "button" || tag === "A") {
    if (tag === "INPUT" && (el.type === "checkbox" || el.type === "radio")) {
      const want = typeof value === "boolean" ? value : value === undefined ? !el.checked : /^(true|on|yes|1)$/i.test(String(value));
      if (el.checked !== want) el.click();
      await sleep(400);
      return { ok: true, control: label, checked: el.checked, ...viewState(null) };
    }
    el.click();
    await sleep(600);
    const pressed = el.getAttribute("aria-pressed");
    return { ok: true, control: label, clicked: true, ...(pressed ? { pressed: pressed === "true" } : {}), ...viewState(null) };
  }
  if (tag === "SELECT") {
    const v = String(value).toLowerCase();
    const opt = Array.from(el.options).find((o) => o.value.toLowerCase() === v || clean(o.textContent).toLowerCase() === v)
      || Array.from(el.options).find((o) => clean(o.textContent).toLowerCase().includes(v));
    if (!opt) return { ok: false, error: `"${value}" is not an option in ${label}.`, options: Array.from(el.options).map((o) => clean(o.textContent)) };
    setNative(el, opt.value, "change");
    await sleep(500);
    return { ok: true, control: label, set_to: clean(opt.textContent), ...viewState(null) };
  }
  if (tag === "INPUT" || tag === "TEXTAREA") {
    let v = value;
    if (el.type === "range" || el.type === "number") {
      v = Number(value);
      if (Number.isNaN(v)) return { ok: false, error: `"${value}" is not a number for ${label}.` };
      if (el.min !== "" && v < Number(el.min)) v = Number(el.min);
      if (el.max !== "" && v > Number(el.max)) v = Number(el.max);
    }
    setNative(el, v, "input");
    await sleep(500);
    return { ok: true, control: label, set_to: el.value, ...viewState(null) };
  }
  return { ok: false, error: `Don't know how to operate "${label}".` };
};

// VOICE CYCLE LOG — every function_call → tool run → function_call_output → response.create → response.done is
// stamped here, kept in a ring buffer (window.__octonVoiceLog) and shipped to the backend (/realtime/log) so a
// session can be audited afterwards. Watchdogs flag the two failure classes we care about:
//   MISSING_CONTINUATION — an output was sent but no response.create followed within 4s
//   CONNECTION_LOST      — data channel / peer connection dropped (e.g. during navigation)
const VLOG = { ring: [], queue: [], session: null, seq: 0 };
const vlog = (type, data = {}) => {
  const e = { t: Date.now(), seq: ++VLOG.seq, type, ...data };
  VLOG.ring.push(e); if (VLOG.ring.length > 500) VLOG.ring.shift();
  VLOG.queue.push(e);
  if (type.includes("WATCHDOG") || type === "error" || type.startsWith("dc.") || type.startsWith("pc.")) console.warn("[octon-voice]", type, data);
  else console.debug("[octon-voice]", type, data);
};
if (typeof window !== "undefined") window.__octonVoiceLog = () => VLOG.ring.slice();
const flushVlog = async () => {
  if (!VLOG.queue.length || !VLOG.session) return;
  const batch = VLOG.queue.splice(0, 200);
  try { await api.post("/realtime/log", { session_id: VLOG.session, page: window.location.pathname, events: batch }); } catch (_) {}
};

// UNIFORM VIEW STATE — attached to EVERY UI tool result so she is never blind between turns: what page/fixture/card,
// what's in view right now, what comes next below the fold, and (for the section she just acted on) its content
// preview + sub-sections. `focusEl` = the section just opened/glowed, if any.
const viewState = (focusEl) => {
  const rep = screenReport();
  const byWhere = (w) => rep.sections.filter((s) => s.where === w).map((s) => `${s.label}${s.state === "loading" ? " (loading)" : ""}`);
  const vs = {
    page: rep.page, fixture_on_screen: rep.fixture_on_screen || null, open_card: rep.open_card, glowing: rep.glowing,
    in_view: byWhere("in_view"), next_below: byWhere("below"), above: byWhere("above"),
    still_loading: rep.still_loading, failed: rep.failed, scroll: rep.scroll,
  };
  if (focusEl) {
    const subs = Array.from(focusEl.querySelectorAll("[data-octon-section]")).map(sectionLabel).filter(Boolean);
    vs.focused = { section: sectionLabel(focusEl), state: focusEl.getAttribute("data-octon-state") || "ready",
      preview: clean(focusEl.innerText).slice(0, 220), sub_sections: subs.slice(0, 12),
      controls: Array.from(focusEl.querySelectorAll("button[data-testid], select[data-testid], input[data-testid]")).filter(isVisible).map((el) => el.getAttribute("data-testid")).slice(0, 12) };
  }
  vs.summary = `${vs.open_card ? `Card open on top: ${vs.open_card}. ` : ""}Page ${vs.page}${vs.fixture_on_screen ? ` · ${vs.fixture_on_screen}` : ""}. In view: ${vs.in_view.join(", ") || "—"}. Next below: ${vs.next_below.join(", ") || "—"}.${vs.focused ? ` Focused: ${vs.focused.section}${vs.focused.sub_sections.length ? ` (sub-sections: ${vs.focused.sub_sections.join(", ")})` : ""}.` : ""}`;
  return vs;
};

// Slow, human-paced scrolling (eased, ~1.1s) — never a jump. amount: little | half | page | end.
const smoothScrollBy = (deltaPx, ms = 1100) => new Promise((resolve) => {
  const se = document.scrollingElement || document.documentElement;
  const start = se.scrollTop;
  const max = Math.max(0, se.scrollHeight - window.innerHeight);
  const target = Math.min(max, Math.max(0, start + deltaPx));
  const dist = target - start;
  if (Math.abs(dist) < 2) return resolve(0);
  const t0 = performance.now();
  const ease = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
  const step = (now) => {
    const k = Math.min(1, (now - t0) / ms);
    se.scrollTop = start + dist * ease(k);
    if (k < 1) requestAnimationFrame(step); else resolve(dist);
  };
  requestAnimationFrame(step);
});
const scrollAmountPx = (amount) => {
  const vh = window.innerHeight;
  const a = (amount || "half").toLowerCase();
  if (a === "little" || a === "bit" || a === "small") return Math.round(vh * 0.28);
  if (a === "page" || a === "full" || a === "lot") return Math.round(vh * 0.85);
  return Math.round(vh * 0.5);
};

const PAGE_MAP = {
  dashboard: "/", home: "/", "super-brain": "/super-brain", superbrain: "/super-brain", brain: "/super-brain",
  advisor: "/advisor", formation: "/advisor", briefing: "/briefing", matchday: "/matchday", live: "/live",
  livematch: "/live", fixtures: "/fixtures", health: "/health", fitness: "/health", player: "/health",
  leagues: "/leagues", console: "/console", codex: "/codex", hive: "/hive", ingest: "/ingest",
  "live-video": "/live-video", video: "/live-video", broadcast: "/live-video", feed: "/live-video", footage: "/live-video",
  // predictive analysis lives on the Advisor (the Match Prediction card) — route these there
  predictions: "/advisor", prediction: "/advisor", predictive: "/advisor", analysis: "/advisor",
  analytics: "/advisor", "predictive-analysis": "/advisor", stats: "/advisor", lineups: "/advisor",
};

// OCTON Voice Live — hands-free WebRTC voice agent (OpenAI Realtime), wired into OCTON's live squad,
// formation & per-player brain via function tools. Every UI tool WAITS for the screen and reports the truth.
export const RealtimeVoiceOrb = () => {
  const [state, setState] = useState("idle"); // idle | connecting | live | listening | thinking | speaking | error
  const [lastTool, setLastTool] = useState("");
  const [muted, setMuted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const highlightApiRef = useRef(null);
  const responseTimer = useRef(null);
  const activeResponse = useRef(false);
  const pendingResponse = useRef(false);
  const inflightTools = useRef(0);
  const awaitingSections = useRef(new Set());
  const stateRef = useRef("idle");
  const outputsAwaitingResponse = useRef(0);   // function_call_outputs sent since the last response.create
  const watchdog = useRef(null);
  useEffect(() => { stateRef.current = state; }, [state]);

  // ONE response per turn: never send response.create while one is active, and never while any tool the
  // model fired is still running — otherwise she speaks off partial results ("now you see it") and again later.
  const flushResponse = useCallback((why = "flush") => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== "open") { vlog("response.create.SKIPPED", { why, reason: "dc not open", dc: dc ? dc.readyState : "none" }); return; }
    if (inflightTools.current > 0) { pendingResponse.current = true; vlog("response.create.DEFERRED", { why, reason: "tools inflight", inflight: inflightTools.current }); return; }
    if (activeResponse.current) { pendingResponse.current = true; vlog("response.create.DEFERRED", { why, reason: "response active" }); return; }
    pendingResponse.current = false;
    try {
      dc.send(JSON.stringify({ type: "response.create" }));
      vlog("response.create.SENT", { why, outputs_covered: outputsAwaitingResponse.current });
      outputsAwaitingResponse.current = 0;
      clearTimeout(watchdog.current);
    } catch (e) { vlog("error", { where: "response.create", message: String(e) }); }
  }, []);
  const scheduleResponse = useCallback((why = "schedule") => {
    clearTimeout(responseTimer.current);
    responseTimer.current = setTimeout(() => flushResponse(why), 350);
  }, [flushResponse]);
  // Watchdog: a function_call_output went out — if no response.create follows within 4s, that is the
  // MISSING_CONTINUATION bug. Log it loudly and force the continuation so she never goes silent.
  const armWatchdog = useCallback(() => {
    clearTimeout(watchdog.current);
    watchdog.current = setTimeout(() => {
      if (outputsAwaitingResponse.current > 0) {
        vlog("WATCHDOG.MISSING_CONTINUATION", { outputs: outputsAwaitingResponse.current, inflight: inflightTools.current, active: activeResponse.current, pending: pendingResponse.current, state: stateRef.current });
        if (inflightTools.current > 0) inflightTools.current = 0;  // a tool must have leaked — unblock
        activeResponse.current = false;
        flushResponse("watchdog");
      }
    }, 4000);
  }, [flushResponse]);
  const sendItem = useCallback((text) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== "open") return false;
    dc.send(JSON.stringify({
      type: "conversation.item.create",
      item: { type: "message", role: "user", content: [{ type: "input_text", text }] },
    }));
    vlog("item.injected", { kind: (text.match(/^\[([A-Z ·a-z]+)/) || [])[1] || "text", len: text.length });
    return true;
  }, []);
  // Stream the current screen's text into the live session as context (aware of every word).
  const sendScreen = useCallback((reason) => {
    const s = captureScreen();
    const vs = viewState(null);
    sendItem(`[SCREEN CONTEXT · ${reason}] ${vs.summary}${vs.still_loading.length ? ` Still building: ${vs.still_loading.join(", ")}.` : ""} This is the exact visible text on his screen right now — stay aware of every word; do not read it aloud unless asked:\n\n${s.text}`);
  }, [sendItem]);

  const teardown = useCallback(() => {
    vlog("session.teardown", { page: window.location.pathname, inflight: inflightTools.current });
    try { dcRef.current && dcRef.current.close(); } catch (_) {}
    try { pcRef.current && pcRef.current.close(); } catch (_) {}
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) {}
    if (audioRef.current) { try { audioRef.current.srcObject = null; } catch (_) {} }
    dcRef.current = null; pcRef.current = null; streamRef.current = null;
    flushVlog();
  }, []);

  useEffect(() => () => teardown(), [teardown]);

  const activeRef = useRef(false);

  // Cue OCTON to react to something the coach just spotlighted on screen (coach-driven glow).
  const cueSpotlight = useCallback((card) => {
    const anchored = card.hasAttribute("data-octon-section") || card.hasAttribute("data-octon-overlay");
    const label = anchored ? (card.getAttribute("data-octon-label") || sectionLabel(card))
      : clean((card.querySelector("h1,h2,h3,h4,h5,.font-tac,.font-head,[data-testid]") || card).textContent).slice(0, 120);
    const state = card.getAttribute("data-octon-state") || "ready";
    let snippet = clean(card.innerText || card.textContent);
    if (snippet.length > 1500) snippet = snippet.slice(0, 1500) + "…";
    const subs = Array.from(card.querySelectorAll("[data-octon-section]")).map(sectionLabel).filter(Boolean).slice(0, 12);
    const controls = Array.from(card.querySelectorAll("button[data-testid], select[data-testid], input[data-testid]")).filter(isVisible).map((el) => el.getAttribute("data-testid")).slice(0, 12);
    const vs = viewState(card);
    const text = `[COACH SPOTLIGHT] The coach just CLICKED to spotlight a section on his screen — it is now glowing icy-blue and is the thing he means by "this" / "here" / "that". `
      + `Page ${vs.page}${vs.fixture_on_screen ? ` · ${vs.fixture_on_screen}` : ""}. Section: "${label}" (state: ${state}${anchored ? "" : ", unanchored card"}). `
      + (subs.length ? `Sub-sections: ${subs.join(", ")}. ` : "") + (controls.length ? `Controls inside: ${controls.join(", ")}. ` : "")
      + `Its full visible content right now:\n${snippet}\n\n`
      + `In ONE short sentence, name exactly what he highlighted so he knows you see it, then ASK if he wants you to walk him through / audit it — and STOP and wait. If he says yes, audit THIS content (quote its actual figures; flag anything odd) — you can re-read it any time with read_screen(wait_for='highlighted').`;
    window.__octonLastSpotlight = { label, state, anchored, subs, controls, text };   // harness/inspection
    if (!sendItem(text)) return;
    vlog("coach.spotlight", { label, state, anchored });
    scheduleResponse("coach-spotlight");
  }, [sendItem, scheduleResponse]);

  // Glacier highlighter — two-way:
  //  (1) OCTON calls highlight_section → the matching on-screen card glows icy-cyan + scrolls into view.
  //  (2) the coach clicks any card while she's live → that card glows and she instantly recognises it.
  useEffect(() => {
    let current = [];
    let lastCard = null, lastAt = 0;
    let lastGlowCard = null;
    const clear = () => { current.forEach((el) => el.classList.remove("octon-glacier")); current = []; };
    const spotlight = (card, fromCoach) => {
      clear();
      card.classList.add("octon-glacier");
      current = [card];
      try {
        if (fromCoach || card !== lastGlowCard) card.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch (_) {}
      lastGlowCard = card;
    };
    // Fuzzy fallback for labels that aren't anchored sections (a player name, a heading). Clears first.
    const fuzzy = (rawLabel) => {
      const label = (rawLabel || "").toLowerCase().trim();
      clear();
      if (!label) { lastGlowCard = null; return null; }
      const root = mainRoot();
      const labelWords = label.split(/[^a-z0-9]+/).filter((w) => w.length > 2);
      const hasWord = (txt, w) => new RegExp(`(^|[^a-z0-9])${w}([^a-z0-9]|$)`).test(txt);
      const cands = Array.from(root.querySelectorAll("h1,h2,h3,h4,h5,[data-testid],.font-tac,.font-head,label,button,p,span,td,th,li,div"));
      let best = null, bestScore = 0;
      for (const el of cands) {
        const txt = clean(el.textContent).toLowerCase();
        const tid = ((el.getAttribute && el.getAttribute("data-testid")) || "").toLowerCase().replace(/[-_]/g, " ");
        let score = 0;
        if (txt && txt.length <= 200) {
          if (txt === label) score = 100;
          else if (hasWord(txt, label) || (txt.length > 3 && label.includes(txt) && txt.length >= label.length * 0.6)) score = 62 - Math.abs(txt.length - label.length) * 0.08;
          else if (labelWords.length) score = 46 * (labelWords.filter((w) => hasWord(txt, w)).length / labelWords.length);
        }
        if (tid && labelWords.length) {
          const tidScore = 72 * (labelWords.filter((w) => hasWord(tid, w)).length / labelWords.length);
          if (tidScore > score) score = tidScore;
        }
        if (score > bestScore) { bestScore = score; best = el; }
      }
      // Only glow when the match is strong — a partial word hit must never light up the wrong card.
      if (best && bestScore >= 58) {
        const card = best.closest("[class*='rounded-']") || best;
        spotlight(card);
        return clean(card.textContent).slice(0, 80);
      }
      lastGlowCard = null;
      return null;
    };
    highlightApiRef.current = { spotlight, fuzzy, clear };
    const onClick = (e) => {
      if (!activeRef.current && !window.__octonHarness) return;
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest("[data-testid='octon-voice-orb']")) return;
      if (t.closest("button,a,input,select,textarea,[role='button'],[role='menuitem'],[role='tab'],[role='option'],[contenteditable]")) return;
      const main = document.querySelector("[data-octon-main]") || document.querySelector("main");
      const overlay = t.closest("[data-octon-overlay]");
      if (!overlay && (!main || !main.contains(t))) return;
      // Nearest ANCHORED section wins (every page is anchored); fall back to the nearest card; inside a player
      // card, the card itself.
      let card = overlay ? (t.closest("[class*='rounded-']") || overlay) : t.closest("[data-octon-section]");
      if (!card) card = t.closest("[class*='rounded-']");
      if (!card || card === main) return;
      const now = Date.now();
      if (card === lastCard && now - lastAt < 1500) return;
      lastCard = card; lastAt = now;
      spotlight(card, true);
      cueSpotlight(card);
    };
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      clear(); highlightApiRef.current = null;
    };
  }, [cueSpotlight]);

  // Glow a section — waits briefly for it if the page is still building it. Always reports the truth.
  const glowSection = useCallback(async (label, waitMs = 5000) => {
    let a = findAnchor(label);
    if (!a) a = await waitFor(() => findAnchor(label), waitMs, 300);
    const hl = highlightApiRef.current;
    if (a) {
      hl && hl.spotlight(a.el);
      await sleep(700);
      const r = { ok: true, matched: true, glowing: a.label, state: a.state, ...viewState(a.el) };
      if (a.state === "loading") r.note = "This section is on screen but its data is STILL BUILDING — say so; do not narrate figures from it yet.";
      if (a.state === "error") r.note = "This section failed to load — tell the coach plainly.";
      return r;
    }
    const fz = hl ? hl.fuzzy(label) : null;
    if (fz) return { ok: true, matched: true, glowing: fz, state: "ready", ...viewState(document.querySelector(".octon-glacier")) };
    const rep = screenReport();
    return { ok: false, matched: false, ...viewState(null),
      note: `"${label}" is NOT on this page. Sections actually on screen: ${rep.on_screen.join(", ") || "none yet"}${rep.still_loading.length ? `; still building: ${rep.still_loading.join(", ")}` : ""}. Do NOT claim you highlighted or scrolled to it — pick one of these real sections, or navigate_app to the page that has it.` };
  }, []);

  // Open a page for a fixture: route changes INSTANTLY (page shows "opening fixture…"), clubs resolve, then the
  // page re-opens on the exact fixture. Returns {ok, fixture} or {ok:false, error}.
  const openFixture = useCallback(async (path, team, opponent, venue) => {
    const samePage = window.location.pathname === path;
    // Already on the page: resolve FIRST (~0.5s) so a request for the fixture that's ALREADY open doesn't tear the
    // page down and rebuild every card (that looked like "stuck"). Different page: change route instantly.
    if (!samePage) navigate(path, { state: { pendingFixture: true } });
    try {
      const { data } = await api.get(`/resolve/matchup?team=${encodeURIComponent(team)}&opponent=${encodeURIComponent(opponent || "")}`, { timeout: 8000 });
      const missing = !data?.team?.id ? team : (opponent && !data?.opponent?.id ? opponent : null);
      if (missing) {
        navigate(path, { replace: true, state: { fixtureError: missing } });
        return { ok: false, error: `Could not find a club called '${missing}'. Ask the coach to confirm the club name — nothing was opened.` };
      }
      const fixture = data.opponent ? `${data.team.name} vs ${data.opponent.name}` : data.team.name;
      const onScreen = document.querySelector("[data-octon-fixture]")?.getAttribute("data-octon-fixture");
      const wanted = `${data.team.id}-${data.opponent?.id || ""}`;
      if (samePage && onScreen === wanted && !venue) return { ok: true, fixture, already_open: true };
      navigate(path, { replace: samePage ? false : true, state: { team_id: data.team.id, opponent_id: data.opponent?.id, league: data.league_id, autorun: true, ...(venue ? { venue } : {}) } });
      return { ok: true, fixture };
    } catch (_) {
      navigate(path, { replace: true, state: { fixtureError: team } });
      return { ok: false, error: "Club lookup timed out — nothing was opened. Tell the coach and offer to retry." };
    }
  }, [navigate]);

  const truthNote = (rep) => {
    const parts = [`The coach can see ONLY these sections right now: ${rep.on_screen.join(", ") || "nothing rendered yet"}.`];
    if (rep.still_loading.length) parts.push(`STILL BUILDING (NOT visible yet — never say he can see them): ${rep.still_loading.join(", ")}. You will get a [SCREEN UPDATE] the moment they land.`);
    if (rep.failed.length) parts.push(`FAILED to load: ${rep.failed.join(", ")}.`);
    return parts.join(" ");
  };

  const executeTool = useCallback(async (item) => {
    let args = {};
    try { args = JSON.parse(item.arguments || "{}"); } catch (_) {}
    setLastTool(`${item.name}(${args.team || args.player || args.page || args.section || ""})`);
    inflightTools.current += 1;
    const t0 = Date.now();
    vlog("tool.start", { name: item.name, args, call_id: item.call_id, inflight: inflightTools.current });
    let result;
    try {
      if (item.name === "navigate_app") {
        const pageKey = (args.page || "").toLowerCase().trim();
        if (pageKey === "reload" || pageKey === "refresh" || pageKey === "rerun") {
          // "Reload the page" hands-free: re-run the current page's main action on the CURRENT fixture (a browser
          // reload would kill the voice session). Pages listen via useVoiceFixture.onRerun.
          window.dispatchEvent(new CustomEvent("octon:rerun"));
          await sleep(1200);
          const rep = screenReport();
          result = { ok: true, reran: window.location.pathname, ...viewState(null), note: `Re-running this page for ${rep.fixture_on_screen || "the current selection"}. ${truthNote(rep)}` };
        } else if (pageKey === "back" || pageKey === "previous") {
          const from = window.location.pathname;
          navigate(-1);
          await waitFor(() => window.location.pathname !== from, 2000, 100);
          await sleep(600);
          const rep = screenReport();
          result = { ok: window.location.pathname !== from, went_back_to: window.location.pathname, from, ...viewState(null), note: truthNote(rep) };
        } else {
        const path = PAGE_MAP[pageKey];
        // GROUNDING GUARD: the fitness page is only opened when the coach actually asked for fitness/health/injuries.
        // (Repeated stress-test failure: the model opened Player Health after a matchplan/lineup request.)
        const said = (args.coach_said || "").toLowerCase();
        const healthWords = /(fit|health|injur|readiness|acwr|load|mindset|morale|wellness|medical|knock|available)/;
        if (!path) { result = { ok: false, error: `unknown page '${args.page}'` }; }
        else if (path === "/health" && !healthWords.test(said)) {
          result = { ok: false, refused: true, ...viewState(null),
            error: `Refused: the coach's words ("${args.coach_said || ""}") do not ask for player fitness/health. Stay on ${window.location.pathname}. For ONE player's fitness/readiness/mind-state call show_player_fitness(team, player) — that pops his card and is ALWAYS allowed. If he asked for the match plan use get_matchplan / page 'matchday'; for line-ups get_both_lineups; for the prediction predict_match.` };
        }
        else {
          let fixture = null;
          if (args.team) {
            const o = await openFixture(path, args.team, args.opponent, args.venue);
            if (!o.ok) result = { ...o, ...viewState(null) };
            else fixture = o.fixture;
          } else {
            navigate(path);
          }
          if (!result) {
            await sleep(500);
            // WAIT for the page to really render before answering — the fixture's line-ups on the advisor, else
            // until every anchored section has finished loading (capped).
            if (fixture && path === "/advisor") await waitFor(() => { const a = findAnchor("lineups"); return a && a.state !== "loading"; }, 15000);
            else if (fixture) {
              // Page switched = fixture label shows the club(s) and the real-fixture strip (if any) has resolved.
              // Do NOT wait for LLM-built content (plan / briefing) — that is reported truthfully as still_loading.
              const first = fixture.split(" vs ")[0].toLowerCase().split(" ").filter((w) => w.length > 2).pop();
              await waitFor(() => {
                const f = (fixtureOnScreen() || "").toLowerCase();
                const banner = document.querySelector("[data-testid='fixture-banner']");
                return f.includes(first) && (!banner || banner.getAttribute("data-octon-state") !== "loading");
              }, 8000, 250);
            }
            else await waitFor(() => { const r = screenReport(); return r.on_screen.length > 0 && !r.busy; }, 6000);
            const rep = screenReport();
            awaitingSections.current = new Set(rep.still_loading);
            // VERIFY the page really switched to the requested fixture — never trust the resolve alone.
            const hasFixtureCtx = !!document.querySelector("[data-octon-fixture]");
            if (fixture && !hasFixtureCtx) {
              result = { ok: true, page: args.page, fixture: null, requested_fixture: fixture, ...viewState(null), note: `This page has no club/fixture context of its own (${fixture} was noted but this page shows the same content for every club). ${truthNote(rep)}` };
            }
            if (fixture && hasFixtureCtx) {
              const fixturePage = (document.querySelector("[data-octon-fixture]")?.getAttribute("data-octon-fixture") || "").includes("-") || ["/advisor", "/matchday", "/live"].includes(path);
              const names = fixture.split(" vs ");
              const want = (fixturePage ? names : names.slice(0, 1)).map((n) => n.toLowerCase().split(" ").filter((w) => w.length > 2).pop());
              const shown = (rep.fixture_on_screen || "").toLowerCase();
              const switched = shown && want.every((w) => w && shown.includes(w));
              if (!switched) {
                const again = await waitFor(() => { const f = (fixtureOnScreen() || "").toLowerCase(); return want.every((w) => w && f.includes(w)) ? f : null; }, 6000, 300);
                if (!again) {
                  result = { ok: false, error: `The page did NOT switch to ${fixturePage ? fixture : names[0]} — it is showing ${rep.fixture_on_screen || "no fixture"}. Tell the coach plainly; then try navigate_app again or set the club pickers with use_control.`, ...viewState(null) };
                }
              }
            }
            if (!result) {
            if (args.section) {
              await waitFor(() => { const a = findAnchor(args.section); return a && a.state === "ready"; }, 8000, 250);
              const g = await glowSection(args.section, 3000);
              rep.section = g.matched ? { glowing: g.glowing, state: g.state } : { not_found: args.section };
            }
            const focus = args.section ? findAnchor(args.section) : null;
            const rep2 = screenReport();
            result = { ok: true, page: args.page, fixture: rep2.fixture_on_screen || null, requested_fixture: fixture, ...(rep.section ? { section: rep.section } : {}), ...viewState(focus ? focus.el : null), note: truthNote(rep2) };
            }
          }
        }
        }
      } else if (item.name === "show_player_fitness") {
        const beforeLabels = openOverlays().map((o) => o.label);
        const norm = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\./g, "").trim();
        const qTok = norm(args.player).split(/\s+/).filter(Boolean);
        const want = qTok[qTok.length - 1] || "";
        // Same surname is NOT the same player: the card's first name/initial must agree with what was asked.
        const isThisPlayer = (label) => {
          const nm = norm(label.replace(/^player card:\s*/i, ""));
          const t = nm.split(/\s+/).filter(Boolean);
          if (!t.length || !want) return false;
          if (qTok.length === 1) return t.includes(want);
          if (t[t.length - 1] !== want) return false;
          return t[0][0] === qTok[0][0];
        };
        window.dispatchEvent(new CustomEvent("octon:show-player", { detail: { team: args.team, player: args.player } }));
        // WAIT for THIS player's card. Accept it the moment it OPENS (even while its data is still loading) —
        // a slow dossier must NOT be misreported as "player not found". Only a DIFFERENT player is a miss.
        const opened = await waitFor(() => {
          const o = openOverlays();
          const mine = o.find((x) => isThisPlayer(x.label));           // correct player, any state (loading or ready)
          if (mine) return mine;
          const fresh = o.find((x) => !beforeLabels.includes(x.label) && x.state === "ready");
          if (fresh && !isThisPlayer(fresh.label) && !/player card$/i.test(fresh.label)) return { wrong: fresh };
          return null;
        }, 8000, 200);
        if (opened && opened.wrong) {
          window.dispatchEvent(new CustomEvent("octon:close-overlays"));
          result = { ok: false, error: `The app resolved '${args.player}' to a DIFFERENT player (${opened.wrong.label.replace(/^Player card:\s*/i, "")}) — card closed. Tell the coach that exact name is not in ${args.team}'s squad data; do not describe the wrong player.`, ...viewState(null) };
        } else if (!opened) {
          result = { ok: false, error: `No fitness card opened for '${args.player}' (${args.team}) — the player was not found in that squad. Check the name/club with the coach; nothing is on screen.`, ...viewState(null) };
        } else {
          const ready = await waitFor(() => (opened.el.getAttribute("data-octon-state") || "ready") === "ready", 12000, 250);
          const label = opened.el.getAttribute("data-octon-label") || "Player card";
          result = ready
            ? { ok: true, open_card: label, card_text: clean(opened.el.innerText).slice(0, 2500), ...viewState(null), note: "This card is now open ON TOP of the page (the page beneath is hidden). Say close_card when the coach is done with it or asks to close/dismiss it." }
            : { ok: false, open_card: `${label} (still loading)`, ...viewState(null), note: "The card opened but its data hasn't loaded yet — say so; do not narrate figures from it." };
        }
      } else if (item.name === "get_current_view") {
        const rep = screenReport();
        const ctl = listControls();
        // A full map of the screen: every section with its state, position, a content preview and its controls.
        const map = inventory().map((s) => ({
          section: s.label, state: s.state, where: rep.sections.find((x) => x.label === s.label)?.where,
          preview: clean(s.el.innerText).slice(0, 160),
          controls: ctl.filter((c) => c.section === s.label).map((c) => c.id),
        }));
        const loose = ctl.filter((c) => !c.section).map((c) => `${c.id}${c.value ? `=${c.value}` : ""}`);
        result = { ok: true, page: rep.page, fixture_on_screen: rep.fixture_on_screen || null, open_card: rep.open_card, glowing: rep.glowing, scroll: rep.scroll,
          sections: map, page_controls: loose, still_loading: rep.still_loading, failed: rep.failed,
          note: rep.open_card ? `A card (${rep.open_card}) is OPEN on top — the coach sees the card, not the page. close_card first if he wants the page.`
            : `In view now: ${rep.sections.filter((s) => s.where === "in_view").map((s) => s.label).join(", ") || "none"}. Below the fold: ${rep.sections.filter((s) => s.where === "below").map((s) => s.label).join(", ") || "none"}. Above: ${rep.sections.filter((s) => s.where === "above").map((s) => s.label).join(", ") || "none"}. Use the section labels with scroll_page/highlight_section and the control ids with use_control.` };
      } else if (item.name === "list_controls") {
        const controls = listControls();
        result = { ok: true, ...viewState(null), controls, note: controls.length ? "Operate any of these with use_control(control=<id or label>, action=click|set, value=...)." : "No controls on this page." };
      } else if (item.name === "use_control") {
        result = await operateControl(args.control, args.action, args.value);
      } else if (item.name === "close_card") {
        const ov = openOverlays();
        const labels = ov.map((o) => o.label);
        window.dispatchEvent(new CustomEvent("octon:close-overlays"));
        // Belt and braces: hit any close button on remaining overlays, then Escape.
        ov.forEach((o) => { const b = o.el.querySelector("[data-testid='close-fitness'],[data-testid='dossier-close'],button[aria-label='Close']"); b && b.click(); });
        document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
        const gone = await waitFor(() => openOverlays().length === 0, 2500, 150);
        result = labels.length
          ? (gone ? { ok: true, closed: labels, ...viewState(null), note: "Card closed — the page beneath is visible again (see in_view)." } : { ok: false, error: `Could not close ${labels.join(", ")}`, ...viewState(null) })
          : { ok: true, closed: [], note: "No card was open — the page itself is what he sees.", ...viewState(null) };
      } else if (item.name === "download_report") {
        window.dispatchEvent(new CustomEvent("octon:download-report"));
        result = { ok: true, note: "PDF download triggered on the current report screen" };
      } else if (item.name === "email_report") {
        const hasBtn = !!document.querySelector("[data-testid$='-email-btn']");
        if (!hasBtn) {
          result = { ok: false, error: "No emailable report on this screen — open the Briefing, Matchday plan or Live match first.", ...viewState(null) };
        } else {
          result = await new Promise((resolve) => {
            const t = setTimeout(() => resolve({ ok: false, error: "Emailing timed out" }), 90000);
            window.dispatchEvent(new CustomEvent("octon:email-report", { detail: { resolve: (r) => { clearTimeout(t); resolve(r); } } }));
          });
          result = { ...result, note: result.ok ? `Report emailed to ${result.to} — say so in one sentence.` : "Tell him honestly it did not send." };
        }
      } else if (item.name === "read_live_video") {
        // Hands-free "read the game now" — trigger a fresh vision read on the Live Video page and speak it.
        if (window.location.pathname !== "/live-video") {
          navigate("/live-video");
          await waitFor(() => !!document.querySelector("[data-testid='live-video-page']"), 4000, 150);
        }
        const hasFeed = !!document.querySelector("[data-testid='feed-meta']");
        if (!hasFeed) {
          result = { ok: false, error: "No video feed is connected yet — the coach needs to paste a live/recorded feed URL and hit Connect on the Live Video page first.", ...viewState(null) };
        } else {
          result = await new Promise((resolve) => {
            const t = setTimeout(() => resolve({ ok: false, error: "The vision read timed out." }), 120000);
            window.dispatchEvent(new CustomEvent("octon:video-read-now", { detail: { resolve: (r) => { clearTimeout(t); resolve(r); } } }));
          });
          result = { ...result, ...viewState(null),
            note: result.ok ? "This is OCTON's live vision read of the frame on screen. Give the VERDICT + advisory in 1-3 sentences; if confidence is low or in_play is false, say you can't read this frame and wait for live play. NEVER invent stats you cannot see." : "Tell the coach the read did not complete." };
        }
      } else if (item.name === "read_screen") {
        let section = null;
        if (args.wait_for) {
          const g = await glowSection(args.wait_for, 12000);
          const a = g.matched ? findAnchor(args.wait_for) : null;
          section = g.matched ? { ...g, text: a ? clean(a.el.innerText).slice(0, 2500) : undefined } : g;
        }
        const s = captureScreen();
        result = { ok: true, ...viewState(section && section.matched ? (findAnchor(args.wait_for) || {}).el : null), title: s.title, ...(section ? { section } : {}), controls: listControls().slice(0, 40), screen_text: s.text };
      } else if (item.name === "scroll_page") {
        const dir = (args.direction || "down").toLowerCase().trim();
        if (args.section) {
          const g = await glowSection(args.section);
          result = g.matched ? { ...g, scrolled_to: g.glowing } : g;
        } else {
          const se = document.scrollingElement || document.documentElement;
          const isEnd = (args.amount || "").toLowerCase() === "end";
          let moved = 0;
          if (dir === "top" || (dir === "up" && isEnd)) moved = await smoothScrollBy(-se.scrollTop, 1400);
          else if (dir === "bottom" || (dir === "down" && isEnd)) moved = await smoothScrollBy(se.scrollHeight, 1400);
          else moved = await smoothScrollBy((dir === "up" ? -1 : 1) * scrollAmountPx(args.amount), 1100);
          await sleep(250);
          const vs = viewState(null);
          result = { ok: true, scrolled: dir, amount: args.amount || "half", moved_px: Math.round(moved), ...vs,
            note: moved === 0 ? `Already at the ${dir === "up" || dir === "top" ? "top" : "bottom"} — nothing more that way.` : `Now in view: ${vs.in_view.join(", ") || "—"}.` };
        }
      } else if (item.name === "highlight_section") {
        if (args.clear) {
          highlightApiRef.current && highlightApiRef.current.clear();
          result = { ok: true, cleared: true };
        } else {
          result = await glowSection(args.section);
        }
      } else {
        try {
          // Two-club overview: open the Advisor on that fixture (instant route, truthful resolve), THEN pull the data.
          // Data tools that BELONG to a page open that page themselves — the model never has to pick the page.
          const PAGE_FOR_TOOL = { get_both_lineups: "/advisor", get_matchup: "/advisor", get_matchplan: "/matchday" };
          let opened = null;
          const toolPage = PAGE_FOR_TOOL[item.name];
          if (toolPage && args.team) {
            opened = await openFixture(toolPage, args.team, args.opponent);
          }
          // Keep her numbers IDENTICAL to the card on screen: same venue as the advisor's selector unless she set one.
          if ((item.name === "predict_match" || item.name === "get_matchup") && !args.venue) {
            const v = document.querySelector("[data-testid='advisor-venue']")?.value;
            if (v) args = { ...args, venue: v };
          }
          const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone) || "Europe/Lisbon";
          const { data } = await api.post("/realtime/tool", { name: item.name, arguments: { ...args, _tz: tz } }, { timeout: 22000 });
          result = data;
          // MODEL CODEX: an explanation is also SHOWN — open Super Brain (if not there), expand the entry, glow it.
          if (item.name === "explain_method" && data && Array.isArray(data.methods) && data.methods.length) {
            try {
              const id = data.methods[0].id;
              if (window.location.pathname !== "/super-brain") navigate("/super-brain");
              await waitFor(() => !!document.querySelector("[data-testid='model-codex-card']"), 8000, 150);
              window.dispatchEvent(new CustomEvent("octon:codex-open", { detail: { id } }));
              await waitFor(() => !!document.querySelector(`[data-testid='codex-body-${id}']`), 4000, 100);
              const g = await glowSection(`codex ${data.methods[0].term}`, 3000);
              result = { ...data, screen: { ...viewState(document.querySelector(`[data-testid='codex-body-${id}']`)), opened_entry: data.methods[0].term, glowing: g.matched ? g.glowing : null,
                next_step: "The Model Codex entry is OPEN and glowing on the Super Brain page — do NOT navigate. Explain it in 1-3 sentences with the live values, then stop." } };
            } catch (e) { vlog("error", { where: "explain_method.ui", message: String(e) }); }
          }
          // COACH TEAM NEWS: re-price the on-screen prediction and glow the Team News block so he sees the number move.
          if (item.name === "set_availability" && data && data.ok) {
            try {
              window.dispatchEvent(new CustomEvent("octon:team-news-changed"));
              await waitFor(() => { const a = findAnchor("team news"); return a && a.state !== "loading"; }, 12000, 200);
              const g = await glowSection("team news", 2000);
              result = { ...data, screen: { ...viewState(g.matched ? findAnchor("team news")?.el : null), glowing: g.matched ? g.glowing : null,
                next_step: g.matched ? "The prediction on screen has RE-PRICED with his team news and the Team News block is glowing — read the new W/D/L from the card (read_screen if needed) in one sentence." : "No prediction card on this page — offer to open the Advisor on the fixture to see the re-priced number." } };
            } catch (e) { vlog("error", { where: "set_availability.ui", message: String(e) }); }
          }
          if (opened) {
            const key = toolPage === "/matchday" ? "matchday plan" : "lineups";
            await waitFor(() => { const a = findAnchor(key); return a && a.state !== "loading"; }, 8000);
            const rep = screenReport();
            awaitingSections.current = new Set(rep.still_loading);
            const nextStep = `The ${toolPage === "/matchday" ? "Matchday" : "Advisor"} page is ALREADY OPEN on this fixture — do NOT call navigate_app (and NEVER open 'health' for a plan/line-up request). Say one sentence about what is on screen, offer to walk through, and wait. Use highlight_section('${toolPage === "/matchday" ? "Matchday Plan" : "Probable Line-ups"}') when he wants it.`;
            result = { ...data, next_step: nextStep, screen: { ...viewState(null), opened: opened.ok ? opened.fixture : opened.error, note: truthNote(rep), next_step: nextStep } };
          } else if (result && typeof result === "object" && !Array.isArray(result) && !result.screen) {
            // Every DATA tool also carries the UI truth, so she can tie the numbers to what he is looking at.
            result = { ...result, screen: viewState(document.querySelector(".octon-glacier")) };
          }
        } catch (e) {
          result = { ok: false, note: "That data is still building — tell the coach it's on its way, keep him engaged, and try again in a moment. Do not invent the content." };
        }
      }
    } catch (e) {
      vlog("error", { where: "tool", name: item.name, message: String(e && e.message || e) });
      result = { ok: false, error: `Tool ${item.name} threw: ${String(e && e.message || e)}` };
    } finally {
      inflightTools.current = Math.max(0, inflightTools.current - 1);
      vlog("tool.done", { name: item.name, call_id: item.call_id, ms: Date.now() - t0, ok: result && result.ok !== false, inflight: inflightTools.current,
        summary: result ? JSON.stringify(result).slice(0, 300) : null });
    }
    return result;
  }, [navigate, openFixture, glowSection]);

  const runTool = useCallback(async (item) => {
    const result = await executeTool(item);
    const dc = dcRef.current;
    if (!dc || dc.readyState !== "open") { vlog("function_call_output.DROPPED", { name: item.name, call_id: item.call_id, dc: dc ? dc.readyState : "none" }); return; }
    try {
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: item.call_id, output: JSON.stringify(result) },
      }));
      outputsAwaitingResponse.current += 1;
      vlog("function_call_output.SENT", { name: item.name, call_id: item.call_id, awaiting: outputsAwaitingResponse.current, inflight: inflightTools.current, active: activeResponse.current });
      armWatchdog();
    } catch (e) { vlog("error", { where: "function_call_output", message: String(e) }); }
    scheduleResponse(`tool:${item.name}`);
  }, [executeTool, scheduleResponse, armWatchdog]);
  // Headless harness for automated testing of the voice→UI tools (no WebRTC needed).
  useEffect(() => {
    window.__octonVoiceTool = (name, args) => executeTool({ name, arguments: JSON.stringify(args || {}) });
    return () => { delete window.__octonVoiceTool; };
  }, [executeTool]);

  // LIVE VIDEO NARRATION: each new video read is spoken hands-free — through Voice Live if she's on
  // the call, otherwise via the browser voice so the advisory is never silent.
  useEffect(() => {
    const onNarrate = (e) => {
      const text = (e.detail && e.detail.text) || "";
      if (!text) return;
      if (activeRef.current && dcRef.current && dcRef.current.readyState === "open") {
        sendItem(`[LIVE VIDEO ADVISORY — say this to the coach now, out loud, in your own natural voice, one or two crisp sentences; do not add anything you cannot see]: ${text}`);
        scheduleResponse("video-narrate");
      } else if (typeof window !== "undefined" && window.speechSynthesis) {
        try { window.speechSynthesis.cancel(); window.speechSynthesis.speak(new SpeechSynthesisUtterance(text)); } catch (_) {}
      }
    };
    window.addEventListener("octon:video-narrate", onNarrate);
    return () => window.removeEventListener("octon:video-narrate", onNarrate);
  }, [sendItem, scheduleResponse]);

  const onEvent = useCallback((raw) => {
    let msg;
    try { msg = JSON.parse(raw); } catch (_) { return; }
    switch (msg.type) {
      case "input_audio_buffer.speech_started":
        setState("listening");
        vlog("speech_started", { inflight: inflightTools.current, active: activeResponse.current });
        break;
      case "input_audio_buffer.speech_stopped": vlog("speech_stopped", {}); break;
      case "response.created":
        setState("thinking"); activeResponse.current = true;
        vlog("response.created", { id: msg.response && msg.response.id, inflight: inflightTools.current, outputs_pending: outputsAwaitingResponse.current });
        break;
      case "response.output_audio.delta":
      case "response.audio.delta": setState("speaking"); break;
      case "response.output_audio_transcript.done":
      case "response.audio_transcript.done": vlog("said", { text: (msg.transcript || "").slice(0, 300) }); break;
      case "conversation.item.input_audio_transcription.completed": vlog("heard", { text: (msg.transcript || "").slice(0, 300) }); break;
      case "response.output_item.done":
        if (msg.item && msg.item.type === "function_call") { vlog("function_call", { name: msg.item.name, call_id: msg.item.call_id, args: msg.item.arguments }); runTool(msg.item); }
        break;
      case "response.done": {
        const st = msg.response && msg.response.status;
        const det = msg.response && msg.response.status_details;
        setState((s) => (s === "speaking" || s === "thinking" ? "live" : s));
        activeResponse.current = false;
        vlog("response.done", { id: msg.response && msg.response.id, status: st, details: det ? JSON.stringify(det).slice(0, 200) : undefined, pending: pendingResponse.current, inflight: inflightTools.current });
        if (st === "failed") vlog("error", { where: "response.failed", details: det ? JSON.stringify(det).slice(0, 300) : "" });
        if (pendingResponse.current) flushResponse("pending-after-done");
        break;
      }
      case "response.cancelled": activeResponse.current = false; vlog("response.cancelled", {}); if (pendingResponse.current) flushResponse("pending-after-cancel"); break;
      case "error": vlog("error", { where: "server", message: JSON.stringify(msg.error || msg).slice(0, 300) }); break;
      default: break;
    }
  }, [runTool, flushResponse]);

  const start = useCallback(async () => {
    if (state !== "idle" && state !== "error") return;
    setState("connecting");
    try {
      const { data } = await api.post("/realtime/session", {});
      const EK = data.client_secret;
      if (!EK) throw new Error("no ephemeral secret");
      VLOG.session = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      vlog("session.start", { model: data.model, page: window.location.pathname });

      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.onconnectionstatechange = () => {
        vlog(`pc.${pc.connectionState}`, { page: window.location.pathname });
        if (pc.connectionState === "failed" || pc.connectionState === "disconnected" || pc.connectionState === "closed") {
          vlog("WATCHDOG.CONNECTION_LOST", { state: pc.connectionState, page: window.location.pathname, inflight: inflightTools.current });
          if (pc.connectionState === "failed") { toast.error("OCTON Voice connection dropped — tap the orb to reconnect"); teardown(); setState("error"); setTimeout(() => setState("idle"), 2500); }
        }
      };
      pc.oniceconnectionstatechange = () => vlog(`pc.ice.${pc.iceConnectionState}`, {});

      const audioEl = new Audio();
      audioEl.autoplay = true;
      audioRef.current = audioEl;
      pc.ontrack = (ev) => { audioEl.srcObject = ev.streams[0]; };

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
      streamRef.current = stream;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;
      dc.onclose = () => vlog("dc.close", { page: window.location.pathname, inflight: inflightTools.current, outputs_pending: outputsAwaitingResponse.current });
      dc.onerror = (e) => vlog("dc.error", { message: String(e && e.error || e) });
      dc.onopen = () => {
        setState("live");
        vlog("dc.open", {});
        setTimeout(() => {
          sendScreen("session start");
          const now = new Date();
          const h = now.getHours();
          const partOfDay = h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
          const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone) || "unknown";
          const localTime = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          sendItem(`[LOCAL CONTEXT] The coach's device timezone is ${tz}; his current LOCAL time is ${localTime} (${partOfDay}). Reason and phrase any time-of-day reference using HIS local time, never yours. Do NOT speak until he speaks; then reply to his actual words.`);
        }, 500);
      };
      dc.onmessage = (e) => onEvent(e.data);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpRes = await fetch(OPENAI_CALLS, {
        method: "POST",
        body: offer.sdp,
        headers: { Authorization: `Bearer ${EK}`, "Content-Type": "application/sdp" },
      });
      if (!sdpRes.ok) throw new Error(`SDP ${sdpRes.status}`);
      const answer = await sdpRes.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
    } catch (e) {
      console.error("voice start failed:", e);
      teardown();
      setState("error");
      toast.error("Could not start OCTON Voice — check the OpenAI key & mic permission");
      setTimeout(() => setState("idle"), 2500);
    }
  }, [state, onEvent, teardown, sendScreen, sendItem]);

  const stop = useCallback(() => { teardown(); setMuted(false); setState("idle"); }, [teardown]);

  const toggleMute = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    setMuted((m) => {
      const next = !m;
      stream.getAudioTracks().forEach((t) => { t.enabled = !next; });
      toast.message(next ? "OCTON muted — she can't hear you" : "OCTON listening again");
      return next;
    });
  }, []);

  const active = ["live", "listening", "thinking", "speaking"].includes(state);
  useEffect(() => { activeRef.current = active; }, [active]);
  // Ship the cycle log to the backend every 5s while live (and once more on stop) so sessions can be audited.
  useEffect(() => {
    if (!active) return;
    const t = setInterval(flushVlog, 5000);
    return () => { clearInterval(t); flushVlog(); };
  }, [active]);
  useEffect(() => { if (activeRef.current) vlog("navigation", { path: location.pathname, key: location.key }); }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps
  // Full-text screen context whenever the coach changes page while OCTON is live.
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => sendScreen("navigation"), 1500);
    return () => clearTimeout(t);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // LIVE SCREEN SYNC — whenever a section lands / changes state, a card opens/closes, or the COACH scrolls to a
  // different part of the page, push a compact [SCREEN UPDATE] with the same view state the tools return. If it's a
  // section she told the coach was still building, she gets to announce it in one sentence.
  useEffect(() => {
    if (!active) return;
    let lastSig = "";
    let timer = null;
    const tick = (why) => {
      const vs = viewState(null);
      const sig = `${vs.page}|${vs.open_card || ""}|${vs.in_view.join(",")}|${vs.still_loading.join(",")}|${vs.failed.join(",")}`;
      if (sig === lastSig) return;
      lastSig = sig;
      const all = vs.in_view.concat(vs.next_below, vs.above).map((l) => l.replace(/ \(loading\)$/, ""));
      const landed = all.filter((l) => awaitingSections.current.has(l) && !vs.still_loading.includes(l));
      landed.forEach((l) => awaitingSections.current.delete(l));
      let text = `[SCREEN UPDATE · ${why}] ${vs.summary}${vs.still_loading.length ? ` Still building: ${vs.still_loading.join(", ")}.` : ""}${vs.failed.length ? ` Failed: ${vs.failed.join(", ")}.` : ""}${vs.glowing ? ` Glowing: ${vs.glowing}.` : ""}`;
      if (landed.length) {
        text += ` → ${landed.join(" and ")} the coach was waiting for ${landed.length > 1 ? "have" : "has"} JUST appeared on his screen. Tell him in ONE short sentence that it's up now, then continue where you left off (do not restart the analysis).`;
      }
      if (!sendItem(text)) return;
      if (landed.length && stateRef.current !== "listening") scheduleResponse("section-landed");
    };
    const obs = new MutationObserver(() => { clearTimeout(timer); timer = setTimeout(() => tick("content changed"), 700); });
    obs.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-octon-state", "data-octon-section", "data-octon-overlay"] });
    const onScroll = () => { clearTimeout(timer); timer = setTimeout(() => tick("coach scrolled"), 900); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { obs.disconnect(); window.removeEventListener("scroll", onScroll); clearTimeout(timer); };
  }, [active, sendItem, scheduleResponse]);

  const label = {
    idle: "OCTON Voice", connecting: "Connecting…", live: "Listening — speak",
    listening: "Hearing you…", thinking: "Thinking…", speaking: "OCTON speaking…", error: "Retry",
  }[state];
  const shownLabel = muted && active ? "Muted — tap mic to resume" : label;
  const ring = state === "speaking" ? "#22d3ee" : state === "listening" ? "#a3e635" : state === "thinking" ? "#f59e0b" : "#22d3ee";

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2" data-testid="octon-voice-orb">
      <AnimatePresence>
        {(active || lastTool) && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            className="glass max-w-[240px] rounded-2xl px-3.5 py-2.5 text-right" data-testid="voice-status">
            <div className="font-tac text-[9px] tracking-widest" style={{ color: ring }}>OCTON VOICE · LIVE</div>
            <div className="mt-0.5 text-xs font-semibold text-white/85">{shownLabel}</div>
            {lastTool && (
              <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-white/45">
                <Radio size={10} /> reading {lastTool}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center gap-2">
        {active && (
          <button onClick={toggleMute} data-testid="voice-mute-btn"
            title={muted ? "Muted — OCTON can't hear you. Click to unmute" : "Mute your mic (e.g. during a call) without ending the session"}
            className={`glass flex h-11 w-11 items-center justify-center rounded-full transition ${muted ? "text-amber-300" : "text-white/70 hover:text-white"}`}>
            {muted ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
        )}
        {active && (
          <button onClick={stop} data-testid="voice-end-btn" title="End voice session"
            className="glass flex h-11 w-11 items-center justify-center rounded-full text-red-300 transition hover:text-red-200">
            <PhoneOff size={18} />
          </button>
        )}
        <button
          onClick={active ? stop : start}
          disabled={state === "connecting"}
          data-testid="voice-orb-btn"
          title={active ? "OCTON Voice is live — click to end" : "Talk to OCTON (voice)"}
          className="relative flex h-16 w-16 items-center justify-center rounded-full"
        >
          {active && (
            <>
              <motion.span className="absolute inset-0 rounded-full" style={{ boxShadow: `0 0 0 2px ${ring}` }}
                animate={{ scale: [1, 1.35, 1], opacity: [0.6, 0, 0.6] }}
                transition={{ duration: state === "speaking" ? 1.1 : 1.8, repeat: Infinity }} />
              <motion.span className="absolute inset-0 rounded-full" style={{ boxShadow: `0 0 0 2px ${ring}` }}
                animate={{ scale: [1, 1.7, 1], opacity: [0.4, 0, 0.4] }}
                transition={{ duration: state === "speaking" ? 1.1 : 1.8, repeat: Infinity, delay: 0.35 }} />
            </>
          )}
          <span className="relative flex h-16 w-16 items-center justify-center rounded-full text-black transition"
            style={{ background: `radial-gradient(circle at 35% 30%, #7dd3fc, ${ring})`,
                     boxShadow: `0 8px 30px ${ring}66, inset 0 0 18px #ffffff55` }}>
            {state === "connecting" ? <Loader2 className="animate-spin" size={22} />
              : <Mic size={22} strokeWidth={2.4} />}
          </span>
        </button>
      </div>
    </div>
  );
};

// Headless harness for automated testing of the voice→UI tools (no WebRTC needed).
if (typeof window !== "undefined") {
  window.__octonScreenReport = screenReport;
  window.__octonFindAnchor = (l) => { const a = findAnchor(l); return a ? { label: a.label, state: a.state } : null; };
}
